import React, { Fragment, useRef, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import {
  FovFieldError,
  FovCheckbox,
  FovTooltip,
  Body,
  VerticalSpacer,
  HorizontalSpacer,
  InfoOutlined,
  CloseOutlined,
  DownOutlined,
} from 'components';
import {
  FormatsFieldLabel,
  FormatsControl,
  FormatsValueBox,
  FormatsTags,
  FormatTag,
  FormatTagRemove,
  FormatsPlaceholder,
  FormatsArrow,
  FormatsPopover,
  FormatsGrid,
  FormatsTooltipContainer,
} from './RPCFormStyles';

// Column-major order to match the two-column layout in the design.
// ENSCAPE is a single option that expands to ENSCAPE_ASSET + ENSCAPE_PACKAGE in metadata.
export const PUBLISH_FORMATS = [
  'RPC',
  'ENSCAPE',
  'GLTF',
  'VRSCENE',
  'DATASMITH',
  'GLTF_ARCHIVE',
  'USDZ',
  'DAE',
  'OBJ',
  'FBX',
  'GLB',
  'USD',
  'BLEND',
  'D5A',
];

// Default selection shared by every publish surface.
export const DEFAULT_PUBLISH_FORMATS = [ 'RPC', 'GLB' ];

// Items fill column-major, so 7 rows splits 14 options into 7 + 7.
const GRID_ROWS = 7;

// Keep the closed box on a single line so the form width never changes.
const MAX_VISIBLE_TAGS = 3;

// Controlled, Formik-agnostic multi-select for publish formats.
// `compact` renders a smaller, single-column variant for narrow side panels.
const PublishFormatsSelect = withTheme(({ theme, options, value, onChange, onOpen, compact }) => {
  const selected = value ?? [];
  const [ opened, setOpened ] = useState(false);
  const [ fixedPos, setFixedPos ] = useState({ top: 0, left: 0, width: 0 });
  const userDataStore = useSelector(state => state.userData);
  const selectedTheme = userDataStore.userPreferences.theme;
  const containerRef = useRef();
  const controlRef = useRef();

  const visibleFormats = selected.slice(0, MAX_VISIBLE_TAGS);
  const hiddenCount = selected.length - visibleFormats.length;

  const updateFixedPosition = useCallback(() => {
    const anchor = controlRef.current;

    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();

    setFixedPos({ top: rect.bottom, left: rect.left, width: rect.width });
  }, []);

  const closeOptions = useCallback(() => setOpened(false), []);

  const toggleOptions = () => {
    if (!opened) {
      updateFixedPosition();
      onOpen?.();
    }

    setOpened(prev => !prev);
  };

  const toggleFormat = format => {
    const next = selected.includes(format)
      ? selected.filter(item => item !== format)
      : [ ...selected, format ];

    onChange(next);
  };

  const removeFormat = (event, format) => {
    event.stopPropagation();
    onChange(selected.filter(item => item !== format));
  };

  useEffect(() => {
    if (!opened) {
      return undefined;
    }

    updateFixedPosition();

    const handleOutsideClick = event => {
      if (!containerRef.current?.contains(event.target)) {
        closeOptions();
      }
    };

    window.addEventListener('resize', updateFixedPosition);
    window.addEventListener('scroll', updateFixedPosition, true);
    document.addEventListener('pointerdown', handleOutsideClick);

    return () => {
      window.removeEventListener('resize', updateFixedPosition);
      window.removeEventListener('scroll', updateFixedPosition, true);
      document.removeEventListener('pointerdown', handleOutsideClick);
    };
  }, [ opened, closeOptions, updateFixedPosition ]);

  return (
    <Fragment>
      <FormatsFieldLabel
        theme={theme}
        $selectedTheme={selectedTheme}
        $compact={compact}
      >
        Publish formats*
        <HorizontalSpacer size={8} />
        <FovTooltip
          position='top'
          bottom={28}
          content={(
            <FormatsTooltipContainer>
              <Body size='X-Small'>
                Select one or more formats to publish this model to.
              </Body>
            </FormatsTooltipContainer>
          )}
        >
          <InfoOutlined width={compact ? 15 : 18} height={compact ? 15 : 18} />
        </FovTooltip>
      </FormatsFieldLabel>
      {!compact && <VerticalSpacer size={10} />}

      <FormatsControl ref={containerRef}>
        <FormatsValueBox
          ref={controlRef}
          theme={theme}
          $selectedTheme={selectedTheme}
          $opened={opened}
          $compact={compact}
          role='button'
          tabIndex={0}
          onClick={toggleOptions}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggleOptions();
            }
          }}
        >
          <FormatsTags>
            {selected.length === 0 ? (
              <FormatsPlaceholder theme={theme} $selectedTheme={selectedTheme}>
                Select formats
              </FormatsPlaceholder>
            ) : (
              <>
                {visibleFormats.map(format => (
                  <FormatTag
                    key={format}
                    theme={theme}
                    $selectedTheme={selectedTheme}
                    $compact={compact}
                  >
                    {format}
                    <FormatTagRemove
                      theme={theme}
                      $selectedTheme={selectedTheme}
                      role='button'
                      onClick={event => removeFormat(event, format)}
                    >
                      <CloseOutlined width={12} height={12} />
                    </FormatTagRemove>
                  </FormatTag>
                ))}
                {hiddenCount > 0 && (
                  <FormatTag theme={theme} $selectedTheme={selectedTheme} $compact={compact}>
                    …
                  </FormatTag>
                )}
              </>
            )}
          </FormatsTags>

          <FormatsArrow $opened={opened}>
            <DownOutlined width={16} height={16} />
          </FormatsArrow>
        </FormatsValueBox>

        {opened && (
          <FormatsPopover
            theme={theme}
            $selectedTheme={selectedTheme}
            $compact={compact}
            $top={fixedPos.top}
            $left={fixedPos.left}
            $width={fixedPos.width}
          >
            <FormatsGrid $rows={GRID_ROWS} $compact={compact}>
              {options.map(format => (
                <FovCheckbox
                  key={format}
                  label={format}
                  checked={selected.includes(format)}
                  onChange={() => toggleFormat(format)}
                />
              ))}
            </FormatsGrid>
          </FormatsPopover>
        )}
      </FormatsControl>
    </Fragment>
  );
});

// Formik adapter used by the publish forms.
const PublishFormatsField = ({ form, options }) => (
  <Fragment>
    <PublishFormatsSelect
      options={options}
      value={form.values?.formats ?? []}
      onChange={next => form.setFieldValue('formats', next)}
      onOpen={() => form.setFieldTouched('formats', true)}
    />
    <FovFieldError name='formats' />
  </Fragment>
);

export { PublishFormatsSelect };
export default PublishFormatsField;
